import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BarChart3, TrendingUp, Compass, ArrowRight, Target, Clock, Shield, Zap, Building, FileCheck, Settings, CheckCircle } from 'lucide-react';

export const Services = () => {
  const { t } = useTranslation();

  const services = [
    {
      icon: BarChart3,
      title: t('services.financial.title'),
      text: t('services.financial.text'),
      features: [
        'Kurumsal finansman danışmanlığı',
        'Finansal modelleme (3 statement model, cash flow, DSCR vb.)',
        'Bilanço ve nakit akışı analizi',
        'Sermaye yapısı optimizasyonu',
        'Stratejik finans planlama',
        'Kredi yapılandırma ve refinansman danışmanlığı',
      ],
    },
    {
      icon: TrendingUp,
      title: t('services.project.title'),
      text: t('services.project.text'),
      features: [
        'Enerji, altyapı, sanayi projeleri için finansal fizibilite',
        'Proje bazlı finansal modelleme',
        'Bankalara sunulacak proje finansmanı dosyaları',
        'DSCR, IRR, NPV analizleri',
        'Lender package hazırlama',
        'EPC, PPA, O&M finansal analiz desteği',
      ],
    },
    {
      icon: Compass,
      title: t('services.sector.title'),
      text: t('services.sector.text'),
      features: [
        'Sektör bazlı finansal ve operasyonel projeksiyon modelleri',
        'Makroekonomik senaryo çalışmaları ve stres testleri',
        'Uluslararası fonlara ve kalkınma bankalarına proje dokümantasyonu',
        'Information memorandum ve pitch deck hazırlama',
        'Financial data room hazırlanması',
        'Uluslararası fon başvuru süreçlerinde danışmanlık',
      ],
    },
  ];

  const steps = [
    { icon: Target, title: t('approach.step1.title'), text: t('approach.step1.text'), num: '01' },
    { icon: BarChart3, title: t('approach.step2.title'), text: t('approach.step2.text'), num: '02' },
    { icon: Shield, title: t('approach.step3.title'), text: t('approach.step3.text'), num: '03' },
    { icon: Clock, title: t('approach.step4.title'), text: t('approach.step4.text'), num: '04' },
  ];

  const intermediarySubServices = [
    {
      icon: Target,
      title: 'Stratejik Finansal Yapılandırma',
      text: 'Her proje, işletmenin mevcut finansal yapısı, sektörel konumu ve büyüme hedefleri dikkate alınarak analiz edilir. Fizibilite çalışmalarından nakit akışı projeksiyonlarına, risk analizinden finansal modellemeye kadar tüm aşamalar tek bir çerçevede ele alınır.',
    },
    {
      icon: Building,
      title: 'Finansmana Erişim Danışmanlığı',
      text: 'Güçlü bankacılık altyapısı ve yıllara yayılan kurumsal ağ sayesinde, projelere en uygun finansman kaynakları tespit edilir.',
      features: ['Banka kredileri ve yapılandırılmış finansman çözümleri', 'Ulusal ve uluslararası fonlar', 'Sübvansiyonlu kredi programları', 'Stratejik iş birlikleri'],
    },
    {
      icon: Zap,
      title: 'Enerji Sektörüne Özel Danışmanlık',
      text: 'Enerji projeleri, teknik bilgi ile finansal bakışın birlikte yürütülmesini gerektirir.',
      features: ['GES, RES ve hibrit enerji projelerinin finansal fizibilitesi', 'Lisans süreçlerinin koordinasyonu', 'Proje fizibilitesi ve kapasite analizleri', 'EPC ve tedarikçi görüşmeleri desteği', 'Enerji satış sözleşmeleri ve PPA analizi'],
    },
    {
      icon: Settings,
      title: 'Operasyonel Danışmanlık',
      text: 'Geliştirilen stratejiler kâğıt üzerinde kalmaz. BEU Danışmanlık, projelerin sahaya yansıyan tüm adımlarında danışmanlık sağlar.',
      features: ['Resmî kurum ve banka süreçlerinin takibi', 'Sözleşme ve teminat yapılarının analizi', 'Nakit akışı ve ödeme planlarının modellenmesi', 'Proje sonrası performans izleme'],
    },
  ];

  const benefits = [
    'Doğru finansman yapısına daha kısa sürede ulaşır',
    'Gereksiz maliyetlerden korunur',
    'Enerji projelerini gerçekçi projeksiyonlarla hayata geçirir',
    'Operasyonel riskleri kontrol altında tutar',
  ];

  return (
    <div data-testid="services-page" className="overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 md:pb-32 bg-[#0F1A2E]">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-20">
          <img
            src="https://images.unsplash.com/photo-1758637612400-09e155a1278c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdvbGQlMjBnZW9tZXRyaWMlMjBzaGFwZXMlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzA2NzE1Nzd8MA&ixlib=rb-4.1.0&q=85"
            alt="Abstract"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0F1A2E]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-6">
              {t('services.title')}
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-none mb-8">
              {t('services.subtitle')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                data-testid={`service-detail-${index}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <service.icon className="text-[#C9A84C] mb-6" size={56} />
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1B2B4B] mb-6">
                    {service.title}
                  </h2>
                  <p className="text-[#1B2B4B]/70 text-lg leading-relaxed mb-8">
                    {service.text}
                  </p>
                  <ul className="space-y-4">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-[#C9A84C] rounded-full mt-2.5 flex-shrink-0" />
                        <span className="text-[#1B2B4B]/70">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="aspect-[4/3] bg-white border border-[#1B2B4B]/10 overflow-hidden rounded-lg shadow-lg">
                    <img
                      src={
                        index === 0
                          ? 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80'
                          : index === 1
                          ? 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800&q=80'
                          : 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=800&q=80'
                      }
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Intermediary Services Section */}
      <section data-testid="intermediary-section" className="py-24 md:py-32 bg-[#1B2B4B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-4">
              Enerji Sektörü Danışmanlığı
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6">
              Enerji Projelerinde Finansal Danışmanlık
            </h2>
            <p className="text-white/70 text-lg max-w-4xl mx-auto leading-relaxed">
              BEU Danışmanlık, enerji sektörüne yönelik proje finansmanı danışmanlığını bütüncül bir hizmet modeliyle yürütür. Amacımız projenin ilk fikrinden operasyonel hayata geçişine kadar tüm aşamalarda stratejik finansal danışmanlık sağlamaktır.
            </p>
          </motion.div>

          {/* Sub Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {intermediarySubServices.map((subService, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#1B2B4B]/50 border border-[#C9A84C]/10 p-8 hover:border-[#C9A84C]/40 transition-all duration-500"
              >
                <subService.icon className="text-[#C9A84C] mb-4" size={40} />
                <h3 className="font-heading text-xl font-bold text-white mb-4">
                  {subService.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  {subService.text}
                </p>
                {subService.features && (
                  <ul className="space-y-2">
                    {subService.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2">
                        <div className="w-1 h-1 bg-[#C9A84C] rounded-full mt-2 flex-shrink-0" />
                        <span className="text-white/50 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </div>

          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="glass p-10"
          >
            <h3 className="font-heading text-2xl font-bold text-white mb-6 text-center">
              Sonuç Odaklı Model
            </h3>
            <p className="text-white/60 text-center mb-8 max-w-3xl mx-auto">
              Bu bütüncül yaklaşım sayesinde müşteriler:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="text-[#C9A84C] flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-white/70 text-sm">{benefit}</span>
                </div>
              ))}
            </div>
            <p className="text-white/50 text-center mt-8 text-sm max-w-4xl mx-auto">
              BEU Danışmanlık, proje finansmanı danışmanlığını klasik bir finans temini süreci olarak değil; işletmenin uzun vadeli büyüme stratejisinin parçası olarak görür. Enerji ve finansın kesişim noktasında konumlanan uzmanlığıyla, projelerin fikir aşamasından sürdürülebilir işletme dönemine kadar güvenilir bir danışman olur.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Approach Section */}
      <section data-testid="approach-section" className="py-24 md:py-32 bg-[#0A1423]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-4">
              {t('approach.title')}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
              {t('approach.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`step-card-${index}`}
                className="relative group glass p-8"
              >
                <div className="text-[#C9A84C]/20 font-heading text-6xl font-bold absolute top-4 right-4 group-hover:text-[#C9A84C]/30 transition-colors">
                  {step.num}
                </div>
                <step.icon className="text-[#C9A84C] mb-4" size={32} />
                <h3 className="font-heading text-xl font-semibold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {step.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0F1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              {t('footer.letsTalk')}
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-10">
              {t('contact.description')}
            </p>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="group inline-flex items-center gap-4 bg-[#C9A84C] text-[#0F1A2E] px-10 py-5 font-semibold text-lg tracking-wide hover:bg-[#E5D49B] transition-all duration-300"
            >
              {t('hero.cta')}
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
